import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Database, CheckCircle, XCircle, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SyncStatus {
  api_source: string;
  sync_status: string;
  last_sync_at: string | null;
  records_synced: number;
  error_message: string | null;
}

export const MedicalDataSyncDashboard = () => {
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncing, setSyncing] = useState<Set<string>>(new Set());

  const loadSyncStatuses = async () => {
    try {
      const { data, error } = await supabase
        .from('medical_api_sync_status')
        .select('*')
        .order('api_source');

      if (error) throw error;
      setSyncStatuses(data || []);
    } catch (error) {
      console.error('Error loading sync statuses:', error);
      toast.error('Failed to load sync statuses');
    } finally {
      setIsLoading(false);
    }
  };

  const triggerSync = async (apiSource: string) => {
    setSyncing(prev => new Set(prev).add(apiSource));
    
    try {
      const { data, error } = await supabase.functions.invoke(`sync-${apiSource}`, {
        body: { trigger: 'manual' }
      });

      if (error) throw error;
      
      toast.success(`${apiSource.toUpperCase()} sync started successfully`);
      
      // Reload statuses after a short delay
      setTimeout(() => {
        loadSyncStatuses();
      }, 2000);
      
    } catch (error) {
      console.error(`Error triggering ${apiSource} sync:`, error);
      toast.error(`Failed to start ${apiSource} sync`);
    } finally {
      setSyncing(prev => {
        const newSet = new Set(prev);
        newSet.delete(apiSource);
        return newSet;
      });
    }
  };

  useEffect(() => {
    loadSyncStatuses();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="outline" className="text-green-600 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'running':
        return <Badge variant="outline" className="text-blue-600 border-blue-200"><Clock className="w-3 h-3 mr-1" />Running</Badge>;
      case 'failed':
        return <Badge variant="outline" className="text-red-600 border-red-200"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  const apiSources = [
    { key: 'icd11', name: 'WHO ICD-11', description: 'International Classification of Diseases' },
    { key: 'pubmed', name: 'PubMed', description: 'Medical research articles' },
    { key: 'trip', name: 'TRIP Database', description: 'Clinical evidence' },
    { key: 'openfda', name: 'OpenFDA', description: 'Drug safety information' },
    { key: 'clinicaltrials', name: 'ClinicalTrials.gov', description: 'Clinical trial data' },
    { key: 'dailymed', name: 'DailyMed', description: 'Drug labeling information' }
  ];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" />
          Loading sync statuses...
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Medical Database Sync Dashboard</h2>
        <p className="text-muted-foreground">
          Manage and monitor syncing of medical databases to populate diagnostic capabilities.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {apiSources.map((source) => {
          const status = syncStatuses.find(s => s.api_source === source.key);
          const isSyncing = syncing.has(source.key);
          
          return (
            <Card key={source.key}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{source.name}</CardTitle>
                  <Database className="w-5 h-5 text-muted-foreground" />
                </div>
                <CardDescription>{source.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    {getStatusBadge(status?.sync_status || 'pending')}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Records:</span>
                    <span className="font-semibold">{status?.records_synced || 0}</span>
                  </div>
                  
                  {status?.last_sync_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Last sync:</span>
                      <span className="text-xs">
                        {new Date(status.last_sync_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  {status?.error_message && (
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                      {status.error_message}
                    </div>
                  )}
                  
                  <Button 
                    onClick={() => triggerSync(source.key)}
                    disabled={isSyncing || status?.sync_status === 'running'}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    {isSyncing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Sync Now
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button 
              onClick={() => apiSources.forEach(source => triggerSync(source.key))}
              disabled={syncing.size > 0}
              variant="default"
            >
              <Database className="w-4 h-4 mr-2" />
              Sync All Databases
            </Button>
            
            <Button 
              onClick={loadSyncStatuses}
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Status
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};